// Boot: pick the storage engine (native SQLite in Tauri, IndexedDB in browsers/webviews),
// open the one Workspace, then render. First run: language + how to start (guided setup · import AVRASTRO data ·
// demo · account · empty) — see modules/onboarding.tsx. The hosted web app (app.avrastro.com) is entered with an
// account instead (sign in · create account · reset) — see modules/gate.tsx.
import { createRoot } from 'react-dom/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { saveSetting, today } from '@vero/domain';
import { CryptoAdapter, IdbAdapter, MemoryAdapter, Workspace, type StorageAdapter } from '@vero/store';
import { createI18n } from '@vero/i18n';
import { APP_VERSION } from '@vero/backup';
import { RuntimeProvider, reportError } from './core/runtime.tsx';
import { TauriSqliteAdapter, tauriInvoke } from './core/tauri-adapter.ts';
import { App } from './app.tsx';
import { seedDemo } from './demo/seed.ts';
import { LockScreen } from './modules/security.tsx';
import { startSyncService } from './core/sync-service.ts';
import { startAccountService } from './core/account-service.ts';
import { Onboarding } from './modules/onboarding.tsx';
import { Gate, HostedSetup, afterSignIn, hostedEntry, useAccountState } from './modules/gate.tsx';
import { initSaveStatus } from './core/save-status.ts';
import { EDITION } from './core/edition.ts';
import './styles/app.css';
import './styles/planner.css';

const params = new URLSearchParams(location.search);

declare const __VERO_PREVIEW__: boolean;
const PREVIEW = typeof __VERO_PREVIEW__ !== 'undefined' && __VERO_PREVIEW__;
/** the hosted demo for reviewers (18 Oct 2026, app.avrastro.com/demo/ behind a password): fictional data seeded on the
 *  first visit and kept in this browser under its own database name, never the real one */
const DEMO = EDITION === 'demo';
const DB_NAME = params.get('db') || (DEMO ? 'vero-demo' : 'vero-os');

/** Storage selection with a safe fallback: if persistent storage cannot be opened (private
 *  window, blocked site data, sandboxed preview), run in memory and say so — never crash. */
async function openWorkspace(unlock: (inner: StorageAdapter) => Promise<StorageAdapter>): Promise<{ ws: Workspace; engine: string; volatile: boolean }> {
  const inv = tauriInvoke();
  const candidates: StorageAdapter[] = [];
  if (inv) candidates.push(new TauriSqliteAdapter(inv));
  else if (!PREVIEW && params.get('storage') !== 'memory' && typeof indexedDB !== 'undefined') candidates.push(new IdbAdapter(DB_NAME));
  for (const a of candidates) {
    try {
      await a.open();
      // encrypted device store → lock screen first (the key never leaves memory)
      const adapter = (await CryptoAdapter.status(a)) ? await unlock(a) : a;
      return { ws: await Workspace.open(adapter, { actor: 'advisor' }), engine: adapter.engine, volatile: false };
    }
    catch (e) { reportError(e, 'storage'); console.warn('[boot] storage unavailable, falling back to memory', e); }
  }
  const mem = new MemoryAdapter();
  return { ws: await Workspace.open(mem, { actor: 'advisor' }), engine: 'memory (not persisted)', volatile: true };
}

/** Auto-lock: after N minutes without interaction the page reloads, which drops the key from memory. */
function armAutoLock(minutes: number) {
  if (!minutes) return;
  let last = Date.now();
  const bump = () => { last = Date.now(); };
  for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart']) window.addEventListener(ev, bump, { passive: true });
  setInterval(() => { if (Date.now() - last > minutes * 60_000) location.reload(); }, 15_000);
}

function Root({ ws, engine }: { ws: Workspace; engine: string }) {
  const [ready, setReady] = useState(() => ws.data.settings.some((s) => s.key === 'onboarded') || ws.data.persons.length > 0);
  // 0.7 — the hosted web app (app.avrastro.com) is entered with an account: sign-in / create account / reset only.
  // The desktop, offline and store editions keep the local-first welcome (guided setup, import, demo, empty).
  const hosted = useMemo(hostedEntry, []);
  const acct = useAccountState(hosted);
  const [lang, setLang] = useState<'el' | 'en'>('el');
  // coming back through the entry (after signing out, or a session that ended) opens Today, not the last screen
  const viaGate = useRef(false);
  useEffect(() => { if (hosted && acct?.signedIn && viaGate.current && ready) { viaGate.current = false; afterSignIn(); } }, [acct?.signedIn]);
  if (hosted) {
    viaGate.current ||= !acct?.signedIn;
    if (!acct?.signedIn) return <Gate acct={acct} onLang={setLang} />;
    if (!ready) return <HostedSetup ws={ws} acct={acct} lang={lang} onDone={() => setReady(true)} />;
  } else if (!ready) return <Onboarding ws={ws} onDone={() => setReady(true)} />;
  return <RuntimeProvider ws={ws} engine={engine}><App /></RuntimeProvider>;
}

/**
 * The hosted app (app.avrastro.com): the offline shell is registered, checked for a new version every 5 minutes and
 * when the tab comes back into view; when a newer version has taken over, the app shows «Νέα έκδοση — Ανανέωση»
 * (event `vero:update-ready`) instead of reloading by itself, so nothing typed is lost.
 */
function watchForUpdates() {
  const sw = navigator.serviceWorker;
  const hadController = !!sw.controller;
  sw.register('sw.js').then((reg) => {
    const check = () => { reg.update().catch(() => undefined); };
    setInterval(check, 5 * 60_000);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
  }).catch(() => undefined);
  sw.addEventListener('controllerchange', () => {
    if (!hadController) return; // the very first install is not an update
    (window as unknown as { __veroUpdate?: boolean }).__veroUpdate = true;
    window.dispatchEvent(new Event('vero:update-ready'));
  });
}

async function boot() {
  const el = document.getElementById('root')!;
  const root = createRoot(el);
  const lang = navigator.language?.startsWith('el') ? 'el' : 'en';
  try {
    const unlock = (inner: StorageAdapter) => new Promise<StorageAdapter>((resolve) => {
      const i18n = createI18n({ locale: lang });
      root.render(<LockScreen locale={{ t: (k, p) => i18n.t(k, p) }} onUnlock={async (secret) => { const a = await CryptoAdapter.unlock(inner, secret); const meta = await CryptoAdapter.status(inner); armAutoLock(meta?.autoLockMinutes ?? 15); resolve(a); }} />);
    });
    const { ws, engine, volatile } = await openWorkspace(unlock);
    if (!PREVIEW) initSaveStatus(ws, { volatile, dbName: DB_NAME, autoSnapshots: params.get('snapshots') !== 'off' });
    (window as unknown as { __vero?: unknown }).__vero = { ws, version: APP_VERSION }; // diagnostics & E2E only
    if ((PREVIEW || DEMO || params.get('demo') === '1') && ws.data.persons.length === 0) { await seedDemo(ws, today(), { profile: DEMO ? 'cyprus' : 'classic' }); await ws.run((v, c) => saveSetting(v, 'onboarded', true, c)); }
    await startSyncService(ws);
    await startAccountService(ws).catch((e) => reportError(e, 'account'));
    // hosted HTTPS builds become an installable app that opens offline (data stays in the encrypted local store)
    if (location.protocol === 'https:' && 'serviceWorker' in navigator && !PREVIEW && !DEMO && !tauriInvoke()) watchForUpdates();
    root.render(<Root ws={ws} engine={engine} />);
  } catch (e) {
    reportError(e, 'boot');
    console.error(e);
    const i18n = createI18n({ locale: lang });
    root.render(<div className="boot"><div className="card card-pad" style={{ maxWidth: 520 }}><h2>{i18n.t('boot.failed')}</h2><pre className="code">{String((e as Error)?.message ?? e)}</pre><a className="btn" href="legacy-exporter.html">{i18n.t('boot.recover')}</a></div></div>);
  }
}
boot();
